import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import useReCaptchaV3 from "./useReCaptChaV3";
import { CallApiVerifyCaptcha_V3 } from "../Api/Enforcement";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";

const ERROR_MESSAGES = {
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  RECAPTCHA_LOADING_ERROR: "Không thể tải reCAPTCHA. Vui lòng tải lại trang.",
  RECAPTCHA_REQUIRED: "Vui lòng hoàn thành xác minh reCAPTCHA.",
};

export default function useReCaptcha(actionName, redirectPath) {
  const isDefaultV3 = process.env.REACT_APP_RECAPTCHA_DEFAULT === "3";
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");
  const reCaptchaV2 = useRef();

  const [isReCaptchaReady, executeReCaptcha] = useReCaptchaV3();
  const [isLoadReCaptChaV3, setIsLoadReCaptChaV3] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(null);
  const recaptchaScriptRef = useRef(null);
  const widgetIdRef = useRef(null);

  // grecaptcha throws "Cannot read properties of null (reading 'style')" when it
  // touches a widget whose container was already removed from the DOM.
  const safeReset = useCallback(() => {
    const container = reCaptchaV2.current;
    if (
      !window.grecaptcha?.reset ||
      widgetIdRef.current === null ||
      !container?.isConnected
    ) {
      return;
    }
    try {
      window.grecaptcha.reset(widgetIdRef.current);
    } catch (e) {
      // widget is gone, nothing to reset
    }
  }, []);

  const handleReCaptchaVerify = useCallback(async () => {
    try {
      setIsLoadReCaptChaV3(true);
      const token = await executeReCaptcha("preTest");

      CallApiVerifyCaptcha_V3(token, (res) => {
        setIsLoadReCaptChaV3(false);
        if (res !== "Success") {
          window.location.replace(redirectPath + "?version=2");
        }
      });
    } catch (error) {
      setIsLoadReCaptChaV3(false);
      ShowCustomErrorModal({ message: ERROR_MESSAGES.RECAPTCHA_INVALID });
      window.location.replace(redirectPath + "?version=2");
    }
  }, [executeReCaptcha, redirectPath]);

  useEffect(() => {
    if (version === "2" || !isDefaultV3) {
      return;
    }

    if (isReCaptchaReady && isDefaultV3) {
      handleReCaptchaVerify();
    }
  }, [handleReCaptchaVerify, isDefaultV3, isReCaptchaReady, version]);

  const renderReCaptcha = useCallback(() => {
    const container = reCaptchaV2.current;
    if (!window.grecaptcha?.render || !container?.isConnected) {
      return;
    }

    // Already rendered into this container: just reset it
    if (widgetIdRef.current !== null && container.hasChildNodes()) {
      safeReset();
      return;
    }

    try {
      widgetIdRef.current = window.grecaptcha.render(container, {
        sitekey: process.env.REACT_APP_RECAPTCHA_KEY_V2,
        callback: () => {
          setRecaptchaError(null);
        },
        "expired-callback": () => {
          setRecaptchaError(
            "Mã xác thực reCAPTCHA đã hết hạn. Vui lòng xác minh lại."
          );
        },
        "error-callback": () => {
          setRecaptchaError(
            "Lỗi tải reCAPTCHA. Vui lòng thử lại hoặc tải lại trang."
          );
        },
      });
    } catch (error) {
      if (!error.message?.includes("already been rendered")) {
        setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
      }
    }
  }, [safeReset]);

  useEffect(() => {
    if (version === "2" || !isDefaultV3) {
      let scriptLoaded = false;
      let checkInterval = null;
      let timeoutId = null;
      let cancelled = false;

      // Create a more robust cleanup function
      const cleanup = () => {
        cancelled = true;
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (recaptchaScriptRef.current) {
          recaptchaScriptRef.current.remove();
          recaptchaScriptRef.current = null;
        }

        // The container is unmounted with the page, so the widget must not be
        // reset here; a fresh render happens on the next mount.
        widgetIdRef.current = null;
      };

      // Load reCAPTCHA script
      const loadRecaptchaScript = () => {
        setRecaptchaLoading(true);
        setRecaptchaError(null);

        // Check if script already exists
        const existingScript = document.querySelector(
          'script[src*="recaptcha/api.js"]'
        );
        if (existingScript) {
          existingScript.remove();
        }

        recaptchaScriptRef.current = document.createElement("script");
        recaptchaScriptRef.current.src =
          "https://www.google.com/recaptcha/api.js?render=explicit";
        recaptchaScriptRef.current.async = true;
        recaptchaScriptRef.current.defer = true;

        recaptchaScriptRef.current.onload = () => {
          if (cancelled) return;
          scriptLoaded = true;

          // Check for grecaptcha to be fully loaded
          checkInterval = setInterval(() => {
            if (window.grecaptcha && window.grecaptcha.render) {
              clearInterval(checkInterval);
              setRecaptchaLoading(false);
              renderReCaptcha();
            }
          }, 100);

          // Set timeout to avoid infinite checking
          timeoutId = setTimeout(() => {
            if (checkInterval) {
              clearInterval(checkInterval);
              if (!window.grecaptcha || !window.grecaptcha.render) {
                setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
                setRecaptchaLoading(false);
              }
            }
          }, 5000);
        };

        recaptchaScriptRef.current.onerror = () => {
          setRecaptchaLoading(false);
          setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
        };

        document.head.appendChild(recaptchaScriptRef.current);
      };

      loadRecaptchaScript();

      // Handle page visibility changes
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && scriptLoaded) {
          // Re-render reCAPTCHA when page becomes visible again
          setTimeout(renderReCaptcha, 300);
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        cleanup();
      };
    }
  }, [version, isDefaultV3, renderReCaptcha]);

  const resetReCaptcha = useCallback(() => {
    if (version === "2" || !isDefaultV3) {
      safeReset();
    }
  }, [version, isDefaultV3, safeReset]);

  const getReCaptchaToken = useCallback(async (action = actionName) => {
    let token = { token_v2: "", token_v3: "" };

    if (version === "2" || !isDefaultV3) {
      // Lấy token v2
      const v2Token =
        widgetIdRef.current !== null
          ? window.grecaptcha?.getResponse(widgetIdRef.current)
          : "";
      if (!v2Token) {
        throw new Error(ERROR_MESSAGES.RECAPTCHA_REQUIRED);
      }
      token.token_v2 = v2Token;
    } else {
      // Lấy token v3
      try {
        token.token_v3 = await executeReCaptcha(action);
      } catch (error) {
        throw new Error(ERROR_MESSAGES.RECAPTCHA_INVALID);
      }
    }

    return token;
  }, [version, isDefaultV3, executeReCaptcha, actionName]);

  return {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  };
}
